import { ReactNode, useEffect, useState } from 'react';
import { PageHelp } from '@/components/ui/PageHelp';
import { Scale, Info, Check, X, Pencil, RotateCcw } from 'lucide-react';
import {
  useHoursBalance,
  useSetOpeningBalance,
  useDeclareBalanceCell,
  useResetBalanceCell,
  BALANCE_TOTAL_CELL,
  type BalanceLine,
} from '@/api/resources';

/**
 * « Balance des heures » — demande du titulaire.
 *
 * Un tableau simple : une ligne par salarié, une colonne par mois sur les
 * 12 derniers mois, et au bout le **solde de départ** et la **balance globale**.
 * Il n'avait jusqu'ici aucun endroit pour voir où en était son équipe sans
 * ouvrir douze fiches navette — ni pour reprendre les heures déjà dues au
 * moment où il a commencé à utiliser le logiciel.
 *
 * Les chiffres viennent du même calcul que la fiche navette : les deux écrans
 * ne peuvent pas se contredire.
 *
 * ## Chaque case se déclare
 *
 * Comme sur la fiche navette : **le calcul propose, le titulaire déclare.**
 * Les douze colonnes mensuelles étaient en lecture seule, et corriger la
 * balance globale passait par un ajustement daté du jour — une écriture qui se
 * propage ailleurs et qu'on ne retire pas d'un clic. Pour redresser un chiffre
 * faux, il ne restait qu'à aller défaire les données du dessous.
 *
 * Désormais une case se clique, s'écrit, et « Revenir au calcul » la remet
 * comme avant : rien, en dessous, n'a bougé. Un mois déclaré déplace la balance
 * globale avec lui — corriger un « −13 » aberrant ne doit pas obliger à
 * corriger le total derrière.
 */
export default function HoursBalancePage() {
  const { data, isLoading } = useHoursBalance(12);

  return (
    <div className="flex flex-col h-full">
      <header className="px-8 py-5 border-b border-slate-200">
        <div className="flex items-center gap-2.5">
          <Scale size={20} className="text-primary" />
          <h1 className="text-[22px] font-semibold tracking-tight">Balance des heures <PageHelp topic="balance" /></h1>
        </div>
        <p className="text-[12.5px] text-slate-500 mt-1">
          Écart entre les heures réalisées et le contrat, mois par mois, sur les 12 derniers mois.
        </p>
      </header>

      <div className="bg-primary-bg/40 border-b border-primary/15 px-8 py-2 text-[11.5px] text-slate-600 flex items-center gap-2">
        <Info size={13} className="text-primary flex-shrink-0" />
        <span>
          <strong>+</strong> = heures dues au salarié · <strong>−</strong> = heures qu'il doit.
          Le <strong>solde de départ</strong> reprend ce que vous lui deviez déjà avant d'utiliser
          le logiciel. La <strong>balance globale</strong> tient compte de tout l'historique, même
          au-delà des 12 mois affichés. <strong>Chaque case se modifie</strong> : le calcul propose,
          vous déclarez — une case déclarée passe en{' '}
          <span className="text-amber-700 font-medium">ambre</span>, et « Revenir au calcul » la
          remet comme avant, sans rien toucher en dessous.
        </span>
      </div>

      <div className="flex-1 overflow-auto p-8">
        {isLoading ? (
          <div className="text-[13px] text-slate-500">Chargement…</div>
        ) : !data || data.lines.length === 0 ? (
          <div className="text-[13px] text-slate-500">Aucun salarié actif.</div>
        ) : (
          <div className="bg-white border border-slate-200 rounded-xl overflow-x-auto">
            <table className="w-full text-[12px] border-collapse">
              <thead>
                <tr className="bg-slate-50">
                  <th className="text-left font-semibold text-slate-700 px-3 py-2.5 sticky left-0 bg-slate-50 border-b border-slate-200 min-w-[170px]">
                    Salarié
                  </th>
                  {data.months.map((m) => (
                    <th
                      key={m.key}
                      className="text-center font-medium text-slate-600 px-2 py-2.5 border-b border-slate-200 whitespace-nowrap"
                    >
                      {m.label}
                    </th>
                  ))}
                  <th className="text-center font-semibold text-slate-700 px-3 py-2.5 border-b border-l border-slate-200 whitespace-nowrap bg-slate-100/70">
                    Heures de départ
                  </th>
                  <th className="text-center font-semibold text-slate-700 px-3 py-2.5 border-b border-slate-200 whitespace-nowrap bg-slate-100/70">
                    Balance globale
                  </th>
                </tr>
              </thead>
              <tbody>
                {data.lines.map((line) => (
                  <Row key={line.employeeId} line={line} monthKeys={data.months.map((m) => m.key)} />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function Row({ line, monthKeys }: { line: BalanceLine; monthKeys: string[] }) {
  return (
    <tr className="hover:bg-slate-50/60">
      <td className="px-3 py-2 sticky left-0 bg-white border-b border-slate-100">
        <div className="flex items-center gap-2">
          <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: line.color }} />
          <span className="font-medium text-slate-900 truncate">{line.name}</span>
          {line.isOwner && <span className="text-[10px] text-slate-400">titulaire</span>}
        </div>
      </td>

      {monthKeys.map((k) => (
        <MonthCell key={k} line={line} monthKey={k} />
      ))}

      <OpeningCell line={line} />
      <TotalCell line={line} />
    </tr>
  );
}

// ============================================================================
// La case qui se déclare
// ============================================================================
// Même langage que la fiche navette, à dessein : fond ambre + pastille pour ce
// qui est déclaré, crayon au survol pour ce qui ne l'est pas, et l'info-bulle
// rappelle toujours ce que le calcul proposait — sans avoir à ouvrir l'éditeur.

const fmt = (n: number) => `${n > 0 ? '+' : ''}${n}`;

/**
 * Coquille d'une case modifiable : affichage, édition en place, retour au calcul.
 *
 * Le bouton « Revenir au calcul » est un FRÈRE du bouton d'édition, jamais son
 * enfant : un bouton dans un bouton n'est pas du HTML valide, et le navigateur
 * s'en sort en avalant l'un des deux clics.
 */
function DeclarableCell({
  value,
  declared,
  computedValue,
  onSave,
  onReset,
  resetTitle = 'Revenir au calcul',
  title,
  render,
  className = '',
}: {
  /** Ce qui est affiché aujourd'hui — déclaré ou calculé. */
  value: number;
  declared: boolean;
  /** Ce que le calcul proposait, pour l'info-bulle. */
  computedValue: number;
  onSave: (hours: number) => Promise<unknown>;
  /** Absent = rien à défaire sur cette case. */
  onReset?: () => Promise<unknown>;
  resetTitle?: string;
  title: string;
  render: (value: number) => ReactNode;
  className?: string;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(String(value));
  const [busy, setBusy] = useState(false);

  // La valeur peut changer sous nos pieds : déclarer un mois déplace le total.
  // Tant qu'on n'édite pas, l'éditeur suit ; pendant l'édition, non — sinon une
  // saisie en cours serait écrasée par un rafraîchissement.
  useEffect(() => {
    if (!editing) setDraft(String(value));
  }, [value, editing]);

  async function commit() {
    const n = Number(draft.replace(',', '.'));
    if (Number.isNaN(n)) {
      setDraft(String(value));
      setEditing(false);
      return;
    }
    if (n !== value) {
      setBusy(true);
      try {
        await onSave(Math.round(n * 10) / 10);
      } finally {
        setBusy(false);
      }
    }
    setEditing(false);
  }

  async function reset() {
    if (!onReset) return;
    setBusy(true);
    try {
      await onReset();
    } finally {
      setBusy(false);
    }
    setEditing(false);
  }

  const cellClass = `relative group/cell border-b border-slate-100 ${declared ? 'bg-amber-50/70' : ''} ${className}`;

  if (editing) {
    return (
      <td className={`px-1.5 py-1.5 text-center ${cellClass}`}>
        <div className="flex items-center gap-1 justify-center">
          <input
            autoFocus
            value={draft}
            disabled={busy}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') void commit();
              if (e.key === 'Escape') {
                setDraft(String(value));
                setEditing(false);
              }
            }}
            inputMode="decimal"
            className="input !py-1 !px-1.5 w-14 text-center tabular-nums text-[12px]"
          />
          <button
            onClick={() => void commit()}
            disabled={busy}
            className="p-1 rounded text-primary hover:bg-primary-bg disabled:opacity-40"
            title="Enregistrer"
          >
            <Check size={13} />
          </button>
          <button
            onClick={() => {
              setDraft(String(value));
              setEditing(false);
            }}
            disabled={busy}
            className="p-1 rounded text-slate-400 hover:bg-slate-200 disabled:opacity-40"
            title="Annuler"
          >
            <X size={13} />
          </button>
        </div>
      </td>
    );
  }

  return (
    <td
      className={`px-2 py-2 text-center tabular-nums ${cellClass}`}
      title={
        declared
          ? `Valeur déclarée à la main. Le calcul proposait : ${fmt(computedValue)} h`
          : title
      }
    >
      {declared && (
        <span
          className="absolute top-0.5 left-0.5 w-1.5 h-1.5 rounded-full bg-amber-500"
          title="Déclaré à la main"
        />
      )}
      <button
        type="button"
        onClick={() => setEditing(true)}
        className="inline-flex items-center gap-1.5 px-1.5 py-0.5 rounded hover:bg-slate-200/60"
      >
        {render(value)}
        <Pencil size={10} className="text-slate-300 opacity-0 group-hover/cell:opacity-100 transition-opacity" />
      </button>
      {declared && onReset && (
        <button
          type="button"
          onClick={() => void reset()}
          disabled={busy}
          title={resetTitle}
          className="absolute top-0 right-0 p-0.5 rounded text-amber-600/70 opacity-0 group-hover/cell:opacity-100 hover:bg-amber-200/70 hover:text-amber-800 transition-opacity disabled:opacity-40"
        >
          <RotateCcw size={10} />
        </button>
      )}
    </td>
  );
}

/** Écart du mois. Déclarable : la valeur posée ici déplace la balance globale. */
function MonthCell({ line, monthKey }: { line: BalanceLine; monthKey: string }) {
  const declare = useDeclareBalanceCell();
  const reset = useResetBalanceCell();

  const value = line.monthly[monthKey] ?? 0;
  const computedValue = line.computed.monthly[monthKey] ?? 0;
  const declared = line.declared[monthKey] !== undefined;
  const adj = line.adjustments[monthKey];

  return (
    <DeclarableCell
      value={value}
      declared={declared}
      computedValue={computedValue}
      onSave={(hours) => declare.mutateAsync({ employeeId: line.employeeId, cell: monthKey, hours })}
      onReset={() => reset.mutateAsync({ employeeId: line.employeeId, cell: monthKey })}
      title={
        adj
          ? `dont ${fmt(adj)} h d'ajustement manuel — cliquer pour déclarer une autre valeur`
          : 'Cliquer pour déclarer une autre valeur'
      }
      render={(v) => (
        <span className={v > 0 ? 'text-primary font-medium' : v < 0 ? 'text-danger font-medium' : declared ? 'text-slate-700 font-medium' : 'text-slate-300'}>
          {/* Un zéro DÉCLARÉ s'écrit « 0 », jamais « — ». Le tiret veut dire
              « rien à signaler » : l'employer ici ferait passer une décision du
              titulaire pour une absence de données. */}
          {v === 0 && !declared ? '—' : fmt(v)}
          {/* L'astérisque ne dit plus rien d'une case déclarée : l'ajustement
              est compris dans le calcul, et ce calcul vient d'être remplacé. */}
          {adj && !declared ? <span className="text-[9px] text-slate-400 align-super ml-0.5">*</span> : null}
        </span>
      )}
    />
  );
}

/**
 * Solde repris à l'installation.
 *
 * Seule case du tableau qui ne se « déclare » pas : il n'y a rien à calculer,
 * c'est une saisie depuis le départ (`Employee.openingHoursBalance`), et le
 * reste de l'application — fiche navette comprise — la lit là. Elle garde donc
 * son écriture propre. « Revenir au calcul » n'y aurait aucun sens ; la remettre
 * à zéro, si.
 */
function OpeningCell({ line }: { line: BalanceLine }) {
  const save = useSetOpeningBalance();
  const v = line.openingHoursBalance;

  return (
    <DeclarableCell
      value={v}
      declared={v !== 0}
      computedValue={0}
      onSave={(hours) => save.mutateAsync({ employeeId: line.employeeId, hours })}
      onReset={v !== 0 ? () => save.mutateAsync({ employeeId: line.employeeId, hours: 0 }) : undefined}
      resetTitle="Remettre à zéro"
      title="Modifier le solde repris à l'ouverture du compte"
      className="border-l border-slate-100 bg-slate-50/40"
      render={(value) =>
        value === 0 ? (
          <span className="text-slate-400">—</span>
        ) : (
          <span className={value < 0 ? 'text-danger' : 'text-primary'}>{fmt(value)} h</span>
        )
      }
    />
  );
}

/**
 * Balance globale.
 *
 * Déclarée, elle remplace : c'est le chiffre que le titulaire annonce au
 * salarié, il ne se déduit de rien. Non déclarée, elle suit le calcul — mois
 * déclarés compris.
 */
function TotalCell({ line }: { line: BalanceLine }) {
  const declare = useDeclareBalanceCell();
  const reset = useResetBalanceCell();
  const declared = line.declared[BALANCE_TOTAL_CELL] !== undefined;

  return (
    <DeclarableCell
      value={line.total}
      declared={declared}
      computedValue={line.computed.total}
      onSave={(hours) =>
        declare.mutateAsync({ employeeId: line.employeeId, cell: BALANCE_TOTAL_CELL, hours })
      }
      onReset={() => reset.mutateAsync({ employeeId: line.employeeId, cell: BALANCE_TOTAL_CELL })}
      title="Cliquer pour déclarer la balance globale"
      className="bg-slate-50/40"
      render={(v) => (
        <span className={`font-semibold ${v > 0 ? 'text-primary' : v < 0 ? 'text-danger' : 'text-slate-400'}`}>
          {v === 0 ? '0' : fmt(v)} h
        </span>
      )}
    />
  );
}
